/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from "axios";
import apiService from "services/apiService";

export interface TopApplication {
  applicationName: string;
  totalDuration: number;
  usagePercentage: number;
}

export interface SummaryDataset {
  totalTime: number;
  sessionCount: number;
  activeTime: number;
  activePercentage: number;
  idleTime: number;
  idlePercentage: number;
  uniqueApplications: number;
  breakTime: number;
  breakPercentage: number;
  top5AppsDisplay: TopApplication[];
  days?: any[];
}

export interface SessionDataset {
  id?: number;
  email?: number | string;
  application: string;
  sessionType: string;
  startTime: string;
  endTime: string;
  duration: number;
  createdAt: string;
  status?: string;
}

export interface UrlSessionDataset {
  id?: number;
  email?: number | string;
  domain: string;
  url: string;
  title: string;
  browser: string;
  startTime: string;
  endTime: string;
  duration: number;
  createdAt: string;
}

export interface ScreenshotDataset {
  filename: string;
  timestamp: string;
  fileSize: number;
  imageUrl: string;
  previewUrl?: string;
  trackingImageUrl?: string;
}

export interface DeviceStatusDataset {
  id: number;
  screenLockEnabled: boolean;
  screenLockTimeout: number;
  diskEncrypted: boolean;
  lastUpdated: string;
  antivirusEnabled?: boolean;
  windowsAutoUpdateEnabled?: boolean;
  windowsUpdatesPending?: number;
  windowsLastUpdateDate?: string;
  osName?: string;
  osVersion?: string;
}

export interface FaceDetectionItem {
  checkedAt: string;
  isFaceDetected: boolean;
  confidenceScore: number;
}

export interface FaceDetectionDataset {
  totalPages: number;
  totalElements: number;
  currentPage: number;
  contents: FaceDetectionItem[];
}

export const formatDuration = (seconds: number | string): string => {
  console.log("Formatting duration for seconds:", seconds);
  const totalSeconds =
    typeof seconds === "string" ? parseFloat(seconds) : seconds;

  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return "0s";
  }

  // Round to whole seconds
  const roundedSeconds = Math.round(totalSeconds);

  const weeks = Math.floor(roundedSeconds / (7 * 24 * 3600));
  const days = Math.floor((roundedSeconds % (7 * 24 * 3600)) / (24 * 3600));
  const hours = Math.floor((roundedSeconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const secs = Math.round(roundedSeconds % 60);
  console.log("Formatted duration parts:", {
    weeks,
    days,
    hours,
    minutes,
    secs,
  });

  const parts: string[] = [];

  if (weeks > 0) parts.push(`${weeks}w`);
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  // Return the most significant units (max 2 units for readability)
  return parts.slice(0, 2).join(" ");
};

export interface FilterParams {
  fromDate?: string;
  toDate?: string;
  status?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchSessionDetails(
  employeeId: string | number,
  filters?: FilterParams,
) {
  console.log("Fetching session details for employee ID:", employeeId);

  try {
    const params = new URLSearchParams();
    // params.append("email", String(employeeId));

    // If date filters are provided, use them in YYYY-MM-DD format; otherwise use today's date in YYYY-MM-DD
    const formatToYMD = (dateStr: string) => {
      // Accepts DD-MM-YYYY or YYYY-MM-DD, returns YYYY-MM-DD
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split("-");
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    };
    if (filters?.fromDate && filters?.toDate) {
      params.append("fromDate", formatToYMD(filters.fromDate));
      params.append("toDate", formatToYMD(filters.toDate));
    } else {
      // Default to past 7 days
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);

      const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
      };

      params.append("fromDate", formatDate(sevenDaysAgo));
      params.append("toDate", formatDate(today));
    }

    if (filters?.status && filters.status !== "all") {
      params.append("status", filters.status);
    }

    // Add pagination parameters
    if (filters?.page) {
      params.append("page", String(filters.page));
    }
    if (filters?.pageSize) {
      params.append("size", String(filters.pageSize));
    }

    const response = await apiService.get(
      `/api/application-session/user/${employeeId}?${params.toString()}`,
    );
    console.log("Session details response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching session details:", error);
    return null;
  }
}

export async function fetchSummaryDetails(
  employeeId: string | number,
  filters?: FilterParams,
) {
  console.log("Fetching summary details for employee ID:", employeeId);

  try {
    const params = new URLSearchParams();
    // params.append("email", String(employeeId));

    // If date filters are provided, use them in YYYY-MM-DD format; otherwise use today's date in YYYY-MM-DD
    const formatToYMD = (dateStr: string) => {
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split("-");
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    };
    if (filters?.fromDate && filters?.toDate) {
      params.append("fromDate", formatToYMD(filters.fromDate));
      params.append("toDate", formatToYMD(filters.toDate));
    } else {
      // Default to past 7 days
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);

      const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
      };

      params.append("fromDate", formatDate(sevenDaysAgo));
      params.append("toDate", formatDate(today));
    }

    if (filters?.status && filters.status !== "all") {
      params.append("status", filters.status);
    }

    // const response = await apiService.get(
    //   `/api/tracking/summary?${params.toString()}`
    // );
    const response = await apiService.get(
      `/api/analytics/session-summary/${employeeId}?${params.toString()}`,
    );
    console.log("Summary details response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching summary details:", error);
    return null;
  }
}

export async function fetchUrlSessionDetails(
  employeeId: string | number,
  filters?: FilterParams,
) {
  console.log("Fetching URL session details for employee ID:", employeeId);

  try {
    const params = new URLSearchParams();
    // params.append("email", String(employeeId));

    // If date filters are provided, use them in YYYY-MM-DD format; otherwise use today's date in YYYY-MM-DD
    const formatToYMD = (dateStr: string) => {
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split("-");
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    };
    if (filters?.fromDate && filters?.toDate) {
      params.append("fromDate", formatToYMD(filters.fromDate));
      params.append("toDate", formatToYMD(filters.toDate));
    } else {
      // Default to past 7 days
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);

      const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
      };

      params.append("fromDate", formatDate(sevenDaysAgo));
      params.append("toDate", formatDate(today));
    }

    if (filters?.status && filters.status !== "all") {
      params.append("status", filters.status);
    }

    // Add pagination parameters
    if (filters?.page) {
      params.append("page", String(filters.page));
    }
    if (filters?.pageSize) {
      params.append("size", String(filters.pageSize));
    }

    // const response = await apiService.get(
    //   `/api/tracking/url-sessions?${params.toString()}`,
    // );
    const response = await apiService.get(
      `/api/browsing-activity/user/${employeeId}?${params.toString()}`,
    );
    console.log("URL session details response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching URL session details:", error);
    return null;
  }
}

export async function fetchDeviceStatus(employeeId: string | number) {
  console.log("Fetching device status for employee ID:", employeeId);

  try {
    const response = await apiService.get(
      `/api/device-status/user/${employeeId}`,
    );
    console.log("Device status response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching device status:", error);
    return null;
  }
}

export async function fetchScreenshots(
  employeeId: string | number,
  filters?: FilterParams,
) {
  console.log("Fetching screenshots for employee ID:", employeeId);

  try {
    const params = new URLSearchParams();
    // params.append("email", String(employeeId));

    // If date filters are provided, use them in YYYY-MM-DD format; otherwise use today's date in YYYY-MM-DD
    const formatToYMD = (dateStr: string) => {
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split("-");
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    };
    if (filters?.fromDate && filters?.toDate) {
      params.append("fromDate", formatToYMD(filters.fromDate));
      params.append("toDate", formatToYMD(filters.toDate));
    } else {
      // Default to past 7 days
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);

      const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
      };

      params.append("fromDate", formatDate(sevenDaysAgo));
      params.append("toDate", formatDate(today));
    }

    if (filters?.status && filters.status !== "all") {
      params.append("status", filters.status);
    }

    // Add pagination parameters
    if (filters?.page) {
      params.append("page", String(filters.page));
    }
    if (filters?.pageSize) {
      params.append("size", String(filters.pageSize));
    }

    // const response = await apiService.get(
    //   `/api/tracking/screenshots?${params.toString()}`,
    // );
    const response = await apiService.get(
      `/api/image-tracking/user/${employeeId}?${params.toString()}`,
    );
    console.log("Screenshots response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching screenshots:", error);
    return null;
  }
}

export async function fetchFaceDetection(
  employeeId: string | number,
  filters?: FilterParams,
): Promise<FaceDetectionDataset | null> {
  try {
    const params = new URLSearchParams();

    // If date filters are provided, use them in YYYY-MM-DD format; otherwise use today's date in YYYY-MM-DD
    const formatToYMD = (dateStr: string) => {
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split("-");
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    };

    if (filters?.fromDate && filters?.toDate) {
      params.append("fromDate", formatToYMD(filters.fromDate));
      params.append("toDate", formatToYMD(filters.toDate));
    } else {
      // Default to past 7 days
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);

      const formatDate = (date: Date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
      };

      params.append("fromDate", formatDate(sevenDaysAgo));
      params.append("toDate", formatDate(today));
    }

    // Add pagination parameters
    if (filters?.page) {
      params.append("page", String(filters.page));
    }
    if (filters?.pageSize) {
      params.append("size", String(filters.pageSize));
    }

    const response = await apiService.get(
      `/api/face-detection/user/${employeeId}?${params.toString()}`,
    );
    console.log("Face detection response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching face detection data:", error);
    return null;
  }
}

export const fetchImageBlob = async (url: string): Promise<string | null> => {
  try {
    const response = await axios.get(url, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    });

    return URL.createObjectURL(response.data);
  } catch (error) {
    console.error("Failed to load image:", error);
    return null;
  }
};

export async function fetchTrackingUserSettings(employeeId: string | number) {
  console.log("Fetching tracking user settings for employee ID:", employeeId);

  try {
    const response = await apiService.get(
      `/api/tracking-user-settings/get/${employeeId}`
    );
    console.log("Tracking user settings response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching tracking user settings:", error);
    return null;
  }
}